import { Injectable } from '@angular/core';
import { BasketOrderService } from './basket-order.service';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { ApplicationConfigService } from './application-config.service';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { from, Observable, of } from 'rxjs';
import { IBaryTableFetchService, TableOptions } from './i-bary-table-fetch-service';
import { BasketOrderView } from '../models/basket-order-view.model';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../models/constants/input.constants';
import { isPresent } from './operators';
import { map } from 'rxjs/operators';
import { createRequestOption, mapAsBody } from '../app/shared/util/request-util';
import { ApiProviderService } from './api-provider.service';
import { TransportOrderRefundView } from '../models/transport-order-refund-view.model';
import { InvoiceStatusEnum } from '../models/enums/invoice-status.enum';

type EntityType = BasketOrderView;
type EntityResponseType = HttpResponse<EntityType>;
type EntityArrayResponseType = HttpResponse<EntityType[]>;

@Injectable({ providedIn: 'root' })
export class BasketOrderExtendedService extends BasketOrderService implements IBaryTableFetchService<EntityType> {
  protected override resourceUrl = API_ENDPOINTS.BASKET_ORDER;
  protected resourceRefundUrl = API_ENDPOINTS.BASKET_ORDER_REFUND;
  protected ressourceNoPaidUrl = API_ENDPOINTS.BASKET_ORDER_BILLS_NO_PAID;

  constructor(protected apiProvider: ApiProviderService, protected override http: HttpClient, protected override applicationConfigService: ApplicationConfigService) {
    super(http, applicationConfigService);
  }

  override create(basketOrder: EntityType): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(basketOrder);
    return this.apiProvider.post<EntityType>(this.resourceUrl, copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  override update(basketOrder: EntityType): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(basketOrder);
    return this.apiProvider.put<EntityType>(`${this.resourceUrl}/${basketOrder.id}`, copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  override partialUpdate(basketOrder: EntityType): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(basketOrder);
    return this.apiProvider.patch<EntityType>(`${this.resourceUrl}/${basketOrder.id}`, copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  override find(id: number): Observable<EntityResponseType> {
    return this.apiProvider.get<EntityType>(`${this.resourceUrl}/${id}`).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  override query(req?: any, serviceOptions?: TableOptions): Observable<HttpResponse<BasketOrderView[]>> {
    serviceOptions?.billType === 'REFUND' ? (req['basketOrderOriginId.specified'] = true) : (req['basketOrderOriginId.specified'] = false);

    req = this.convertQueryDateFromClient(req);
    const options = createRequestOption(req);

    return this.apiProvider
      .get<BasketOrderView[]>(this.resourceUrl, {
        params: options,
        observe: 'response',
      })
      .pipe(
        map((res: HttpResponse<BasketOrderView[]>) => {
          return new HttpResponse<BasketOrderView[]>({
            body: (res.body || []).map(v => new BasketOrderView(v)),
            url: res.url as string,
            headers: res.headers,
            status: res.status,
          });
        })
      );
  }

  override delete(id: number): Observable<HttpResponse<{}>> {
    return this.apiProvider.delete(`${this.resourceUrl}/${id}`);
  }

  override addBasketOrderToCollectionIfMissing(basketOrderCollection: EntityType[], ...basketOrdersToCheck: (EntityType | null | undefined)[]): EntityType[] {
    const basketOrders: EntityType[] = basketOrdersToCheck.filter(isPresent);
    if (basketOrders.length > 0) {
      const basketOrderCollectionIdentifiers = basketOrderCollection.map(basketOrderItem => basketOrderItem.id);
      const basketOrdersToAdd = basketOrders.filter(basketOrderItem => {
        const basketOrderIdentifier = basketOrderItem.id;
        if (basketOrderIdentifier == null || basketOrderCollectionIdentifiers.includes(basketOrderIdentifier)) {
          return false;
        }
        basketOrderCollectionIdentifiers.push(basketOrderIdentifier);
        return true;
      });
      return [...basketOrdersToAdd, ...basketOrderCollection];
    }
    return basketOrderCollection;
  }

  fillDataToShow(entities: BasketOrderView[], tableOptions?: TableOptions): Observable<BasketOrderView[]> {
    if (tableOptions?.isModerator) {
      return from(entities).pipe(map(() => entities));
    } else {
      return of(entities);
    }
  }

  private convertQueryDateFromClient(query: any): any {
    if (!query) {
      return null;
    }

    const keys = Object.keys(query || {});
    const billDateKeys = keys.filter(k => k.includes('billDate'));
    if (billDateKeys.length) {
      for (const billDateKey of billDateKeys) {
        query[billDateKey] = query[billDateKey] ? dayjs(query[billDateKey]) : undefined;
      }
    }
    return query;
  }

  protected override convertDateFromClient(basketOrder: EntityType): EntityType {
    return Object.assign({}, basketOrder, {
      billDate: basketOrder.billDate?.isValid() ? basketOrder.billDate.format(DATE_FORMAT) : undefined,
      dueDate: basketOrder.dueDate?.isValid() ? basketOrder.dueDate.format(DATE_FORMAT) : undefined,
    });
  }

  protected override convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.billDate = res.body.billDate ? dayjs(res.body.billDate) : undefined;
      res.body.dueDate = res.body.dueDate ? dayjs(res.body.dueDate) : undefined;
    }
    return res;
  }

  protected override convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((basketOrder: EntityType) => {
        basketOrder.billDate = basketOrder.billDate ? dayjs(basketOrder.billDate) : undefined;
        basketOrder.dueDate = basketOrder.dueDate ? dayjs(basketOrder.dueDate) : undefined;
      });
    }
    return res;
  }

  getBasketOrderRefunds(id: number): Observable<TransportOrderRefundView[]> {
    return this.apiProvider.get<TransportOrderRefundView[]>(`${this.resourceRefundUrl}/${id}`).pipe(mapAsBody);
  }

  public findBillsNoPaid(customerId: number): Observable<BasketOrderView[]> {
    return this.apiProvider.get<BasketOrderView[]>(this.ressourceNoPaidUrl(customerId)).pipe(mapAsBody);
  }

  public changeStatus(basketOrderId: number, status: InvoiceStatusEnum): Observable<any> {
    return this.apiProvider.put<any>(`/basket-order/${basketOrderId}/status/${status}`, {}).pipe(mapAsBody);
  }
}
